import { useState } from "react";
import axios from "axios";
import { Lock, Loader2, CheckCircle2 } from "lucide-react";
import { QUALIFICATIONS, TECHNICAL_DOMAINS, NON_TECHNICAL_DOMAINS } from "@/data/internships";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAME_RE = /^[A-Za-z][A-Za-z .'-]{1,}$/;
const PHONE_RE = /^\d{10}$/;

export default function ApplicationForm({ defaultDomain = "", compact = false }) {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    qualification: "",
    domain: defaultDomain,
    college: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ loading: false, ok: false, error: "" });

  const setField = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((e) => ({ ...e, [name]: "" }));
  };

  const onName = (e) => {
    const v = e.target.value.replace(/[0-9]/g, "");
    setField("full_name", v);
  };
  const onPhone = (e) => {
    const v = e.target.value.replace(/\D/g, "").slice(0, 10);
    setField("phone", v);
  };

  const validate = () => {
    const errs = {};
    if (!NAME_RE.test(form.full_name.trim())) errs.full_name = "Enter a valid name (letters only, min 2 chars).";
    if (!PHONE_RE.test(form.phone)) errs.phone = "Phone must be exactly 10 digits.";
    if (!form.qualification) errs.qualification = "Please select a qualification.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus({ loading: true, ok: false, error: "" });
    try {
      await axios.post(`${API}/applications`, form);
      setStatus({ loading: false, ok: true, error: "" });
      setForm({
        full_name: "",
        email: "",
        phone: "",
        qualification: "",
        domain: defaultDomain,
        college: "",
        message: "",
      });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : (typeof detail === "string" ? detail : "Something went wrong. Please try again.");
      setStatus({ loading: false, ok: false, error: msg });
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      data-testid="application-form"
      className={`bg-white ${compact ? "" : "border border-neutral-200 rounded-2xl p-8 md:p-10"}`}
    >
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.24em] text-neutral-500">Application Form</p>
          <h3 className="font-serif text-3xl md:text-4xl mt-2 tracking-tight">Apply for Internship</h3>
          <p className="text-sm text-neutral-500 mt-2">Complete the form below.</p>
        </div>
        <span className="hidden md:inline-flex text-xs font-mono uppercase tracking-[0.2em] px-3 py-1.5 rounded-full border border-neutral-200 text-neutral-600">
          2 minute form
        </span>
      </div>

      <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
        <Field label="Full Name" error={errors.full_name}>
          <input
            data-testid="app-input-full-name"
            required
            name="full_name"
            value={form.full_name}
            onChange={onName}
            placeholder="Your name"
            autoComplete="name"
            className="editorial-input"
          />
        </Field>
        <Field label="Email">
          <input
            data-testid="app-input-email"
            required
            type="email"
            name="email"
            value={form.email}
            onChange={(e) => setField("email", e.target.value)}
            placeholder="you@email.com"
            autoComplete="email"
            className="editorial-input"
          />
        </Field>
        <Field label="Phone (10 digits)" error={errors.phone}>
          <input
            data-testid="app-input-phone"
            required
            name="phone"
            value={form.phone}
            onChange={onPhone}
            placeholder="9876543210"
            inputMode="numeric"
            maxLength={10}
            autoComplete="tel"
            className="editorial-input"
          />
        </Field>
        <Field label="Qualification" error={errors.qualification}>
          <select
            data-testid="app-input-qualification"
            required
            name="qualification"
            value={form.qualification}
            onChange={(e) => setField("qualification", e.target.value)}
            className="editorial-input bg-white"
          >
            <option value="">Select qualification</option>
            {QUALIFICATIONS.map((q) => (
              <option key={q} value={q}>{q}</option>
            ))}
          </select>
        </Field>
        <Field label="Preferred Domain">
          <select
            data-testid="app-input-domain"
            name="domain"
            value={form.domain}
            onChange={(e) => setField("domain", e.target.value)}
            className="editorial-input bg-white"
          >
            <option value="">Select a domain</option>
            <optgroup label="Technical Domains">
              {TECHNICAL_DOMAINS.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </optgroup>
            <optgroup label="Non-Technical Domains">
              {NON_TECHNICAL_DOMAINS.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </optgroup>
          </select>
        </Field>
        <Field label="College / Institute">
          <input
            data-testid="app-input-college"
            name="college"
            value={form.college}
            onChange={(e) => setField("college", e.target.value)}
            placeholder="Your college name"
            className="editorial-input"
          />
        </Field>
        <div className="md:col-span-2">
          <Field label="Message (optional)">
            <textarea
              data-testid="app-input-message"
              name="message"
              value={form.message}
              onChange={(e) => setField("message", e.target.value)}
              rows={3}
              placeholder="Tell us anything else we should know"
              className="editorial-input resize-none"
            />
          </Field>
        </div>
      </div>

      <div className="mt-10 flex flex-col md:flex-row items-start md:items-center gap-4 md:justify-between">
        <button
          type="submit"
          disabled={status.loading}
          data-testid="app-submit-button"
          className="btn-pill btn-pill--primary w-full md:w-auto"
        >
          {status.loading ? (
            <><Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.75} /> Sending...</>
          ) : status.ok ? (
            <><CheckCircle2 className="w-4 h-4" strokeWidth={1.75} /> Submitted</>
          ) : (
            "Submit Application"
          )}
        </button>
        <div className="flex items-center gap-2 text-xs text-neutral-500">
          <Lock className="w-3.5 h-3.5" strokeWidth={1.5} />
          <span>Your information is encrypted and securely stored.</span>
        </div>
      </div>

      {status.ok && (
        <div
          data-testid="app-success-msg"
          className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-900 px-4 py-3 text-sm"
        >
          Application received. Our team will get back within 24 hours.
        </div>
      )}
      {status.error && (
        <div
          data-testid="app-error-msg"
          className="mt-6 rounded-xl border border-red-200 bg-red-50 text-red-900 px-4 py-3 text-sm"
        >
          {status.error}
        </div>
      )}
    </form>
  );
}

function Field({ label, error, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.18em] text-neutral-500 mb-1">{label}</span>
      {children}
      {error && (
        <span className="block mt-1 text-xs text-red-600" data-testid="field-error">
          {error}
        </span>
      )}
    </label>
  );
}
